// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Outsize — menu, help, about, and the results screens. The results show EVERY
// player's streak AND the pair that ended each run with the correct answer
// (principle #9 — "what everyone missed").

import { AREA_NOTE, POP_NOTE, STATS, country } from './data';
import { escapeHtml } from './dom';
import { fmtStat } from './format';
import { biggerSide, type GameState } from './game';
import { MODE_IDS, modeOf, type Mode } from './modes';
import type { Pair } from './seed';
import type { PeerResult } from './race';

export const ATTRIB =
  'Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a> · ' +
  '<a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a>';

function modeChips(active: string): string {
  return MODE_IDS.map(
    (id) =>
      `<button class="chip${id === active ? ' on' : ''}" data-mode="${id}" aria-pressed="${id === active}">${escapeHtml(modeOf(id).name)}</button>`,
  ).join('');
}

export function menuHtml(o: { modeId: string; best: number; muted: boolean; dateStr: string }): string {
  const mode = modeOf(o.modeId);
  return `
    <section class="screen menu">
      <h1 class="title">Outsize</h1>
      <p class="tagline">Which country is bigger? Keep the streak going.</p>
      <div class="modes" role="group" aria-label="Mode">${modeChips(o.modeId)}</div>
      <p class="mode-blurb">${escapeHtml(mode.blurb)}</p>
      <p class="best">Best streak · <strong>${o.best}</strong></p>
      <div class="menu-actions">
        <button class="btn primary" id="playRandom">Play</button>
        <button class="btn" id="playDaily">Daily gauntlet · ${escapeHtml(o.dateStr)}</button>
        <button class="btn" id="playFriends">Play with friends</button>
        <div class="menu-minor">
          <button class="linkbtn" id="help">How to play</button>
          <button class="linkbtn" id="about">About</button>
          <button class="linkbtn" id="mute">${o.muted ? 'Sound: off' : 'Sound: on'}</button>
        </div>
      </div>
    </section>`;
}

export function helpHtml(): string {
  return `
    <button class="sheet-close" aria-label="Close">×</button>
    <h2>How to play</h2>
    <p>Two countries. Tap the one you think is <strong>bigger</strong> — by
      <strong>population</strong> (Populace), <strong>area</strong> (Expanse), or
      whatever the round asks (Motley).</p>
    <ul>
      <li>Get it right and your <strong>streak</strong> grows.</li>
      <li>The gap between the two shrinks every round, so it gets harder the longer you last.</li>
      <li><strong>One wrong call ends the run.</strong></li>
      <li>Play a friend and you both race the <strong>same</strong> countries.</li>
    </ul>
    <p class="fine">Desktop: ← / → (or A / L) pick left / right.</p>`;
}

export function aboutHtml(): string {
  return `
    <button class="sheet-close" aria-label="Close">×</button>
    <h2>About</h2>
    <p><strong>Outsize</strong> is a higher-or-lower streak game about the size of
      countries — population, land area, and how crowded they are.</p>
    <p class="fine">${escapeHtml(POP_NOTE)} ${escapeHtml(AREA_NOTE)} Country outlines and
      facts are from Natural Earth (public domain); nothing is fetched at runtime.</p>
    <p class="fine">Multiplayer is peer-to-peer over WebRTC — a public signalling relay
      only brokers the first connection between players; no game data is stored on any
      server, and there are no cookies or trackers beyond an anonymous page-view count.</p>
    <p class="fine">${ATTRIB}</p>`;
}

/** "picked X, answer was Y (A 10M vs B 8M)" for the pair that ended a run. */
function missLineHtml(seq: Pair[], missedIdx: number, missedPick: number): string {
  if (missedIdx < 0 || missedIdx >= seq.length) return '';
  const p = seq[missedIdx];
  const left = country(p.leftId);
  const right = country(p.rightId);
  if (!left || !right) return '';
  const bigger = biggerSide(p);
  const label = STATS[p.stat].label;
  const chosen = missedPick === 0 ? left : missedPick === 1 ? right : null;
  const answer = bigger === 'left' ? left : right;
  const lv = `<span class="mv">${escapeHtml(left.name)}</span> ${escapeHtml(fmtStat(p.leftVal, p.stat))}`;
  const rv = `<span class="mv">${escapeHtml(right.name)}</span> ${escapeHtml(fmtStat(p.rightVal, p.stat))}`;
  const pickTxt = chosen
    ? `picked ${escapeHtml(chosen.name)}`
    : 'ran out of time';
  return `<div class="miss">
      <div class="miss-q">Slipped on <b>${escapeHtml(label)}</b>: ${lv} · ${rv}</div>
      <div class="miss-a">${pickTxt} — bigger was <b>${escapeHtml(answer.name)}</b></div>
    </div>`;
}

export function soloResultsHtml(state: GameState, mode: Mode, best: number, daily: boolean): string {
  const maxed = state.reason === 'maxed';
  const head = maxed ? 'You maxed it!' : 'Streak over';
  const fa = state.answers[state.answers.length - 1];
  const missIdx = fa && !fa.correct ? fa.idx : -1;
  const missPick = fa ? (fa.picked === 'left' ? 0 : fa.picked === 'right' ? 1 : -1) : -1;
  return `
    <section class="screen results">
      <h2 class="results-head">${head}</h2>
      <div class="big-streak">${state.streak}<span class="bs-lbl"> streak</span></div>
      <p class="best">Best · <strong>${Math.max(best, state.streak)}</strong> · ${escapeHtml(mode.name)}${daily ? ' · daily' : ''}</p>
      ${missLineHtml(state.seq, missIdx, missPick)}
    </section>`;
}

export function multiResultsHtml(
  ranked: PeerResult[],
  selfSeat: number | null,
  mode: Mode,
  seq: Pair[],
  tally: number[],
): string {
  const rows = ranked
    .map((r, i) => {
      const me = r.seat === selfSeat;
      const wins = tally[r.seat] ?? 0;
      const status = r.left ? '<span class="left-tag">left</span>' : r.over ? '' : '<span class="live-tag">playing…</span>';
      const miss = r.over && !r.left ? missLineHtml(seq, r.missedIdx, r.missedPick) : '';
      return `
        <div class="res-row${me ? ' me' : ''}${i === 0 && !r.left ? ' lead' : ''}">
          <div class="res-top">
            <span class="res-rank">${i + 1}</span>
            <span class="res-name">${escapeHtml(r.name)}${me ? ' (you)' : ''}</span>
            <span class="res-streak">${r.streak}${wins ? ` · ${wins}★` : ''} ${status}</span>
          </div>
          ${miss}
        </div>`;
    })
    .join('');
  return `<h2 class="results-head">${escapeHtml(mode.name)} · results</h2><div class="res-list">${rows}</div>`;
}
